import { useEffect } from 'react';

/**
 * Easter egg: Feedy marches in on stubby legs, blasts lime bolts at the
 * Channable cube, and topples it. Plays once (~7.5s) then calls onDone.
 * Fixed to the bottom-left corner, pointer-events:none — never blocks the UI.
 *
 * Self-contained: the Channable cube is its real logo geometry (6 polygons,
 * recreated from their logo-small.svg, not hotlinked), Feedy is the brand mark.
 */
const DURATION = 7600;

// Bolt fire times (s). Travel ~0.45s → cube shakes baked into fb-cube at
// matching %: 2.45/7≈35%, 3.15/7≈45%, 3.85/7≈55%.
const SHOTS = [2.0, 2.7, 3.4];

const CSS = `
.fb-stage{position:fixed;left:16px;bottom:14px;width:340px;height:150px;z-index:60;pointer-events:none;overflow:visible;font-size:0}
.fb-ground{position:absolute;left:0;bottom:8px;width:320px;height:10px;border-radius:50%;
  background:radial-gradient(ellipse at center, rgba(0,0,0,.14), transparent 70%)}

/* ---- Feedy ---- */
.fb-feedy{position:absolute;left:6px;bottom:10px;animation:fb-move 7s ease-out both}
.fb-body{position:relative;width:54px;height:70px;animation:fb-hop 7s ease-in-out both}
.fb-mark{position:absolute;top:0;left:0;filter:drop-shadow(0 2px 3px rgba(0,0,0,.25))}
.fb-gun{position:absolute;top:30px;left:46px;width:18px;height:7px;border-radius:3px;background:#16181D;
  animation:fb-recoil 7s ease-out both}
.fb-gun::after{content:"";position:absolute;right:-3px;top:1px;width:4px;height:5px;border-radius:1px;background:#C6F24E}
.fb-legs{position:absolute;bottom:0;left:14px;width:28px;height:16px}
.fb-leg{position:absolute;top:0;width:4.5px;height:15px;border-radius:3px;background:#16181D;transform-origin:top center}
.fb-leg::after{content:"";position:absolute;bottom:-1px;left:-1px;width:7px;height:4px;border-radius:2px;background:#16181D}
.fb-leg-l{left:4px;animation:fb-step .42s ease-in-out infinite}
.fb-leg-r{left:16px;animation:fb-step .42s ease-in-out infinite;animation-delay:-.21s}

/* ---- bolts ---- */
.fb-bolt{position:absolute;left:60px;bottom:36px;width:13px;height:5px;border-radius:3px;background:#C6F24E;
  box-shadow:0 0 8px 1px rgba(198,242,78,.9);animation:fb-bolt .45s linear both}

/* ---- Channable cube (the target) ---- */
.fb-channable{position:absolute;left:250px;bottom:14px;width:62px;height:62px;transform-origin:bottom center;
  animation:fb-cube 7s ease-in-out both}
.fb-boom{position:absolute;left:244px;bottom:30px;font-size:38px;line-height:1;animation:fb-boom .9s ease-out both;animation-delay:4.15s}

@keyframes fb-move{
  0%{transform:translateX(-180px);opacity:0}
  7%{opacity:1}
  18%{transform:translateX(0)}
  86%{transform:translateX(0);opacity:1}
  100%{transform:translateX(80px);opacity:0}
}
@keyframes fb-hop{
  0%,63%{transform:translateY(0)}
  67%{transform:translateY(-16px)}
  72%{transform:translateY(0)}
  76%{transform:translateY(-9px)}
  80%,100%{transform:translateY(0)}
}
@keyframes fb-recoil{
  0%,27%{transform:translateX(0) rotate(0)}
  29%{transform:translateX(-4px) rotate(-6deg)}
  33%{transform:translateX(0) rotate(0)}
  38%{transform:translateX(-4px) rotate(-6deg)}
  42%{transform:translateX(0) rotate(0)}
  48%{transform:translateX(-4px) rotate(-6deg)}
  52%,100%{transform:translateX(0) rotate(0)}
}
@keyframes fb-step{0%,100%{transform:rotate(22deg)}50%{transform:rotate(-22deg)}}
@keyframes fb-bolt{
  0%{transform:translateX(0);opacity:0}
  15%{opacity:1}
  80%{opacity:1}
  100%{transform:translateX(186px);opacity:0}
}
@keyframes fb-cube{
  0%{opacity:0;transform:translateY(-12px) rotate(0)}
  6%{opacity:1;transform:translateY(0)}
  34%{transform:translate(0,0) rotate(0)}
  36%{transform:translate(5px,0) rotate(4deg)}
  39%{transform:translate(-4px,0) rotate(-4deg)}
  42%{transform:translate(0,0) rotate(0)}
  44%{transform:translate(5px,0) rotate(4deg)}
  47%{transform:translate(-5px,0) rotate(-4deg)}
  50%{transform:translate(0,0) rotate(0)}
  54%{transform:translate(6px,0) rotate(5deg)}
  57%{transform:translate(-6px,0) rotate(-5deg)}
  60%{transform:translate(0,0) rotate(0);opacity:1}
  100%{transform:translate(46px,86px) rotate(96deg);opacity:0}
}
@keyframes fb-boom{
  0%{transform:scale(.2);opacity:0}
  30%{transform:scale(1.1);opacity:1}
  100%{transform:scale(1.7);opacity:0}
}
@media (prefers-reduced-motion: reduce){.fb-stage{display:none}}
`;

function ChannableCube() {
  // Real Channable logo geometry (logo-small.svg), 6 polygons, 0 0 140 140.
  return (
    <svg viewBox="0 0 140 140" width="62" height="62" aria-hidden="true">
      <polygon fill="#FAC51F" points="35 60 0 80 0 120 35 140 70 120 35 100" />
      <polygon fill="#E4388E" points="105 60 105 100 70 120 105 140 140 120 140 80" />
      <polygon fill="#109CD9" points="105 60 105 20 70 0 35 20 35 60 70 40" />
      <polygon fill="#EC771A" points="35 60 35 100 70 120 70 80" />
      <polygon fill="#9E216D" points="105 60 70 80 70 120 105 100" />
      <polygon fill="#27418E" points="105 60 70 40 35 60 70 80" />
    </svg>
  );
}

function FeedyGuy() {
  return (
    <div className="fb-feedy">
      <div className="fb-body">
        <svg className="fb-mark" viewBox="0 0 54 54" width="54" height="54" aria-hidden="true">
          <rect width="54" height="54" rx="13" fill="#C6F24E" />
          <path d="M12 14 L12 42 L40 42 Z" fill="#16181D" />
          {/* determined eyes (white on the dark triangle), looking right */}
          <circle cx="20" cy="32" r="4.2" fill="#fff" />
          <circle cx="21.8" cy="32" r="2" fill="#16181D" />
          <circle cx="28.5" cy="35" r="3.4" fill="#fff" />
          <circle cx="30" cy="35" r="1.6" fill="#16181D" />
        </svg>
        <div className="fb-gun" />
        <div className="fb-legs">
          <span className="fb-leg fb-leg-l" />
          <span className="fb-leg fb-leg-r" />
        </div>
      </div>
    </div>
  );
}

export default function FeedyBattle({ onDone }) {
  useEffect(() => {
    const t = setTimeout(() => onDone?.(), DURATION);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="fb-stage" aria-hidden="true">
      <style>{CSS}</style>
      <div className="fb-ground" />
      <FeedyGuy />
      {SHOTS.map((d, i) => (
        <span key={i} className="fb-bolt" style={{ animationDelay: `${d}s` }} />
      ))}
      <div className="fb-channable"><ChannableCube /></div>
      <div className="fb-boom">💥</div>
    </div>
  );
}
